'use client'

import { useState, useRef, useCallback, useEffect, useContext } from 'react'
import { WaveformVisualizer } from './waveform-visualizer'
import { RecordingControls, RecordingStatus } from './recording-controls'
import { SocketContext } from '@/lib/providers/socket-provider'
import { toast } from 'react-toastify'

// Types for Web Speech API
interface SpeechRecognitionEvent extends Event {
    results: SpeechRecognitionResultList
    resultIndex: number
}

interface SpeechRecognitionResultList {
    length: number
    item(index: number): SpeechRecognitionResult
    [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
    isFinal: boolean
    length: number
    item(index: number): SpeechRecognitionAlternative
    [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionAlternative {
    transcript: string
    confidence: number
}

interface SpeechRecognition extends EventTarget {
    continuous: boolean
    interimResults: boolean
    lang: string
    maxAlternatives: number
    start(): void
    stop(): void
    abort(): void
    onresult: ((event: SpeechRecognitionEvent) => void) | null
    onerror: ((event: Event & { error: string }) => void) | null
    onend: (() => void) | null
    onstart: (() => void) | null
}

declare global {
    interface Window {
        SpeechRecognition: new () => SpeechRecognition
        webkitSpeechRecognition: new () => SpeechRecognition
    }
}

type TranscriptionMode = 'browser' | 'whisper'

interface AudioRecorderProps {
    onRecordingComplete: (audioBlob: Blob, duration: number) => void
    onTranscriptionUpdate?: (text: string, isFinal: boolean) => void
    onError: (error: string) => void
    maxDuration?: number
    enableRealTimeTranscription?: boolean
    transcriptionMode?: TranscriptionMode // 'browser' = Web Speech API, 'whisper' = OpenAI via Socket
    consultationId?: number // Required for whisper mode
    className?: string
}

function formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${minutes.toString().padStart(2, '0')}:${secs
        .toString()
        .padStart(2, '0')}`
}

export function AudioRecorder({
    onRecordingComplete,
    onTranscriptionUpdate,
    onError,
    maxDuration = 3600,
    enableRealTimeTranscription = false,
    transcriptionMode = 'whisper', // Default to whisper for better accuracy
    consultationId,
    className = '',
}: AudioRecorderProps) {
    const { socket, isConnected } = useContext(SocketContext)

    const [isRecording, setIsRecording] = useState(false)
    const [isPaused, setIsPaused] = useState(false)
    const [duration, setDuration] = useState(0)
    const [audioLevel, setAudioLevel] = useState(0)
    const [waveformData, setWaveformData] = useState<number[]>([])
    const [permissionDenied, setPermissionDenied] = useState(false)
    const [speechRecognitionSupported, setSpeechRecognitionSupported] = useState(true)
    const [whisperSessionId, setWhisperSessionId] = useState<string | null>(null)
    const [whisperAvailable, setWhisperAvailable] = useState(false)
    const whisperSessionIdRef = useRef<string | null>(null)

    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const audioContextRef = useRef<AudioContext | null>(null)
    const analyserRef = useRef<AnalyserNode | null>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const chunksRef = useRef<Blob[]>([])
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const animationFrameRef = useRef<number | null>(null)
    const speechRecognitionRef = useRef<SpeechRecognition | null>(null)
    const transcriptRef = useRef<string>('')
    const isRecordingRef = useRef<boolean>(false)
    const whisperSegmentChunksRef = useRef<Blob[]>([]) // Chunks do segmento atual
    const whisperIntervalRef = useRef<NodeJS.Timeout | null>(null)
    const segmentRecorderRef = useRef<MediaRecorder | null>(null) // Recorder separado para segmentos Whisper

    const cleanup = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
            animationFrameRef.current = null
        }
        if (audioContextRef.current) {
            audioContextRef.current.close()
            audioContextRef.current = null
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop())
            streamRef.current = null
        }
        if (speechRecognitionRef.current) {
            try {
                speechRecognitionRef.current.abort()
            } catch (e) {
                // Ignore errors on abort
            }
            speechRecognitionRef.current = null
        }
        if (whisperIntervalRef.current) {
            clearInterval(whisperIntervalRef.current)
            whisperIntervalRef.current = null
        }
        if (segmentRecorderRef.current && segmentRecorderRef.current.state !== 'inactive') {
            try {
                segmentRecorderRef.current.stop()
            } catch (e) {
                // Ignore
            }
        }
        segmentRecorderRef.current = null
        whisperSegmentChunksRef.current = []
        mediaRecorderRef.current = null
        analyserRef.current = null
        isRecordingRef.current = false
    }, [])

    // Socket listeners for Whisper transcription (with consultationId for multi-tenant)
    useEffect(() => {
        if (!socket || transcriptionMode !== 'whisper' || !consultationId) return

        const handleStarted = (data: { sessionId: string; consultationId: number }) => {
            setWhisperSessionId(data.sessionId)
            whisperSessionIdRef.current = data.sessionId
            setWhisperAvailable(true)
            console.log('[Whisper] Session started:', data.sessionId, 'for consultation:', data.consultationId)
        }

        const handleResult = (data: { text: string; fullText: string; consultationId: number }) => {
            console.log('[Whisper] Transcription result:', data.text)
            transcriptRef.current = data.fullText
            if (onTranscriptionUpdate) {
                onTranscriptionUpdate(data.fullText, true)
            }
        }

        const handleError = (data: { error: string; code?: string; consultationId: number }) => {
            console.error('Whisper transcription error:', data.error)
            // Falha de trecho é tentada de novo e fica só no log. Falta de crédito
            // não se resolve sozinha: a pessoa precisa saber por que não transcreve.
            if (data.code === 'AI_CREDITS_EXHAUSTED') toast.error(data.error)
        }

        const handleStopped = (data: { finalText: string; consultationId: number }) => {
            if (onTranscriptionUpdate && data.finalText) {
                onTranscriptionUpdate(data.finalText, true)
            }
            setWhisperSessionId(null)
            whisperSessionIdRef.current = null
        }

        // Listen to events specific to this consultation
        const startedEvent = `transcription:started:${consultationId}`
        const resultEvent = `transcription:result:${consultationId}`
        const errorEvent = `transcription:error:${consultationId}`
        const stoppedEvent = `transcription:stopped:${consultationId}`

        socket.on(startedEvent, handleStarted)
        socket.on(resultEvent, handleResult)
        socket.on(errorEvent, handleError)
        socket.on(stoppedEvent, handleStopped)

        console.log('[Whisper] Listening to events for consultation:', consultationId)

        return () => {
            socket.off(startedEvent, handleStarted)
            socket.off(resultEvent, handleResult)
            socket.off(errorEvent, handleError)
            socket.off(stoppedEvent, handleStopped)
        }
    }, [socket, transcriptionMode, onTranscriptionUpdate, consultationId])

    // Iniciar novo segmento de gravacao para Whisper
    const startNewWhisperSegment = useCallback(() => {
        if (!streamRef.current || !isRecordingRef.current) return

        // Parar segmento anterior se existir
        if (segmentRecorderRef.current && segmentRecorderRef.current.state !== 'inactive') {
            segmentRecorderRef.current.stop()
        }

        whisperSegmentChunksRef.current = []

        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : 'audio/webm'

        const segmentRecorder = new MediaRecorder(streamRef.current, { mimeType })

        segmentRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) {
                whisperSegmentChunksRef.current.push(e.data)
            }
        }

        segmentRecorder.onstop = () => {
            // Quando o segmento para, enviar para transcricao
            sendWhisperSegment()
        }

        segmentRecorderRef.current = segmentRecorder
        segmentRecorder.start()
        console.log('[Whisper] Started new audio segment')
    }, [])

    // Enviar segmento completo para transcricao
    const sendWhisperSegment = useCallback(async () => {
        const currentSessionId = whisperSessionIdRef.current
        if (!socket || whisperSegmentChunksRef.current.length === 0 || !currentSessionId) {
            return
        }

        const chunks = whisperSegmentChunksRef.current
        const audioBlob = new Blob(chunks, { type: 'audio/webm' })

        // Verificar tamanho minimo
        if (audioBlob.size < 5000) {
            console.log('[Whisper] Segment too small, skipping:', audioBlob.size, 'bytes')
            return
        }

        console.log('[Whisper] Sending complete segment, size:', audioBlob.size, 'bytes')

        const reader = new FileReader()
        reader.onloadend = () => {
            const base64 = (reader.result as string).split(',')[1]
            if (base64) {
                socket.emit('transcription:audio', {
                    audioData: base64,
                    sessionId: currentSessionId,
                    consultationId,
                })
            }
        }
        reader.readAsDataURL(audioBlob)
    }, [socket, consultationId])

    // Ciclo de gravacao de segmentos para Whisper
    const cycleWhisperSegment = useCallback(() => {
        if (!isRecordingRef.current || isPaused) return

        // Parar segmento atual (vai disparar onstop que envia o audio)
        if (segmentRecorderRef.current && segmentRecorderRef.current.state === 'recording') {
            segmentRecorderRef.current.stop()
        }

        // Iniciar novo segmento apos pequeno delay
        setTimeout(() => {
            if (isRecordingRef.current && !isPaused) {
                startNewWhisperSegment()
            }
        }, 100)
    }, [isPaused, startNewWhisperSegment])

    useEffect(() => {
        return () => {
            cleanup()
        }
    }, [cleanup])

    useEffect(() => {
        if (duration >= maxDuration && isRecording) {
            stopRecording()
        }
    }, [duration, maxDuration, isRecording])

    const visualizeAudio = useCallback(() => {
        if (!analyserRef.current) return

        const analyser = analyserRef.current
        const dataArray = new Uint8Array(analyser.frequencyBinCount)

        const draw = () => {
            if (!isRecording || isPaused) return

            analyser.getByteFrequencyData(dataArray)

            const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length
            setAudioLevel(average / 255)

            setWaveformData(prev => {
                const newData = [...prev, average]
                if (newData.length > 200) {
                    return newData.slice(-200)
                }
                return newData
            })

            animationFrameRef.current = requestAnimationFrame(draw)
        }

        draw()
    }, [isRecording, isPaused])

    const initSpeechRecognition = useCallback(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

        if (!SpeechRecognition) {
            setSpeechRecognitionSupported(false)
            console.warn('Web Speech API not supported in this browser')
            return null
        }

        const recognition = new SpeechRecognition()
        recognition.continuous = true
        recognition.interimResults = true
        recognition.lang = 'pt-BR'
        recognition.maxAlternatives = 1

        recognition.onresult = (event: SpeechRecognitionEvent) => {
            let interimTranscript = ''
            let finalTranscript = transcriptRef.current

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i]
                const transcript = result[0].transcript

                if (result.isFinal) {
                    finalTranscript += transcript + ' '
                    transcriptRef.current = finalTranscript
                    if (onTranscriptionUpdate) {
                        onTranscriptionUpdate(finalTranscript.trim(), true)
                    }
                } else {
                    interimTranscript += transcript
                    if (onTranscriptionUpdate) {
                        onTranscriptionUpdate(
                            (finalTranscript + interimTranscript).trim(),
                            false
                        )
                    }
                }
            }
        }

        recognition.onerror = (event) => {
            console.error('Speech recognition error:', event.error)
            // Don't show error for "no-speech" as it's normal during pauses
            if (event.error !== 'no-speech' && event.error !== 'aborted') {
                console.warn(`Speech recognition error: ${event.error}`)
            }
        }

        recognition.onend = () => {
            // Restart recognition if still recording (speech recognition stops after silence)
            if (isRecordingRef.current && speechRecognitionRef.current) {
                try {
                    speechRecognitionRef.current.start()
                } catch (e) {
                    // Ignore errors when restarting
                }
            }
        }

        return recognition
    }, [onTranscriptionUpdate])

    const startRecording = async () => {
        try {
            setPermissionDenied(false)
            chunksRef.current = []
            setWaveformData([])
            setDuration(0)

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 16000,
                    channelCount: 1,
                },
            })

            streamRef.current = stream

            // Setup AudioContext for visualization
            audioContextRef.current = new AudioContext({ sampleRate: 16000 })
            const source = audioContextRef.current.createMediaStreamSource(stream)
            const analyser = audioContextRef.current.createAnalyser()
            analyser.fftSize = 256
            source.connect(analyser)
            analyserRef.current = analyser

            // Setup MediaRecorder
            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? 'audio/webm;codecs=opus'
                : MediaRecorder.isTypeSupported('audio/webm')
                    ? 'audio/webm'
                    : 'audio/mp4'

            const mediaRecorder = new MediaRecorder(stream, { mimeType })

            mediaRecorder.ondataavailable = e => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data)
                }
            }

            mediaRecorder.onstop = () => {
                const mimeTypeToUse = mediaRecorder.mimeType || 'audio/webm'
                const blob = new Blob(chunksRef.current, { type: mimeTypeToUse })
                onRecordingComplete(blob, duration)
                cleanup()
            }

            mediaRecorder.onerror = event => {
                console.error('MediaRecorder error:', event)
                onError('Erro durante a gravacao')
                cleanup()
            }

            mediaRecorderRef.current = mediaRecorder
            mediaRecorder.start(1000)
            setIsRecording(true)
            setIsPaused(false)
            isRecordingRef.current = true

            // Start timer
            timerRef.current = setInterval(() => {
                setDuration(prev => prev + 1)
            }, 1000)

            // Start visualization
            setTimeout(() => {
                visualizeAudio()
            }, 100)

            // Start speech recognition if enabled
            if (enableRealTimeTranscription && onTranscriptionUpdate) {
                transcriptRef.current = ''

                if (transcriptionMode === 'whisper' && socket && isConnected && consultationId) {
                    // Use Whisper transcription via Socket
                    whisperSegmentChunksRef.current = []
                    console.log('[Whisper] Starting transcription session for consultation:', consultationId)
                    socket.emit('transcription:start', { consultationId })

                    // Aguardar um pouco para a sessao ser criada, depois iniciar primeiro segmento
                    setTimeout(() => {
                        if (isRecordingRef.current) {
                            startNewWhisperSegment()
                        }
                    }, 500)

                    // Setup interval to cycle segments every 8 seconds
                    // Each segment is a complete webm file that Whisper can process
                    whisperIntervalRef.current = setInterval(() => {
                        cycleWhisperSegment()
                    }, 8000)
                } else if (transcriptionMode === 'browser') {
                    // Use browser's Web Speech API
                    const recognition = initSpeechRecognition()
                    if (recognition) {
                        speechRecognitionRef.current = recognition
                        try {
                            recognition.start()
                        } catch (e) {
                            console.error('Error starting speech recognition:', e)
                        }
                    }
                }
            }
        } catch (err: any) {
            console.error('Error starting recording:', err)
            if (err.name === 'NotAllowedError') {
                setPermissionDenied(true)
                onError(
                    'Permissao de microfone negada. Por favor, permita o acesso ao microfone nas configuracoes do navegador.'
                )
            } else if (err.name === 'NotFoundError') {
                onError(
                    'Nenhum microfone encontrado. Conecte um microfone e tente novamente.'
                )
            } else if (err.name === 'NotReadableError') {
                onError(
                    'Microfone em uso por outro aplicativo. Feche outros aplicativos e tente novamente.'
                )
            } else {
                onError(`Erro ao iniciar gravacao: ${err.message}`)
            }
        }
    }

    const pauseRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.pause()
            setIsPaused(true)
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current)
                animationFrameRef.current = null
            }
            // Stop speech recognition on pause
            if (speechRecognitionRef.current) {
                try {
                    speechRecognitionRef.current.stop()
                } catch (e) {
                    // Ignore errors
                }
            }
            // Pause Whisper - stop current segment and interval
            if (whisperIntervalRef.current) {
                clearInterval(whisperIntervalRef.current)
                whisperIntervalRef.current = null
            }
            if (segmentRecorderRef.current && segmentRecorderRef.current.state === 'recording') {
                segmentRecorderRef.current.stop() // Vai enviar o segmento atual
            }
        }
    }

    const resumeRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
            mediaRecorderRef.current.resume()
            setIsPaused(false)
            timerRef.current = setInterval(() => {
                setDuration(prev => prev + 1)
            }, 1000)
            visualizeAudio()
            // Restart speech recognition on resume
            if (speechRecognitionRef.current) {
                try {
                    speechRecognitionRef.current.start()
                } catch (e) {
                    // Ignore errors
                }
            }
            // Resume Whisper - start new segment and interval
            if (transcriptionMode === 'whisper' && enableRealTimeTranscription && !whisperIntervalRef.current) {
                startNewWhisperSegment()
                whisperIntervalRef.current = setInterval(() => {
                    cycleWhisperSegment()
                }, 8000)
            }
        }
    }

    const stopRecording = () => {
        isRecordingRef.current = false

        // Stop browser speech recognition
        if (speechRecognitionRef.current) {
            try {
                speechRecognitionRef.current.abort()
            } catch (e) {
                // Ignore errors
            }
            speechRecognitionRef.current = null
        }

        // Stop Whisper transcription interval
        if (whisperIntervalRef.current) {
            clearInterval(whisperIntervalRef.current)
            whisperIntervalRef.current = null
        }

        // Stop and send final segment
        const currentSessionId = whisperSessionIdRef.current
        if (transcriptionMode === 'whisper' && segmentRecorderRef.current) {
            if (segmentRecorderRef.current.state === 'recording') {
                // Stopping will trigger onstop which sends the final segment
                segmentRecorderRef.current.stop()
            }
            segmentRecorderRef.current = null
        }

        // Stop transcription session after a small delay to allow final segment to be sent
        if (socket && currentSessionId) {
            setTimeout(() => {
                socket.emit('transcription:stop', { sessionId: currentSessionId, consultationId })
            }, 500)
        }

        setWhisperSessionId(null)
        whisperSessionIdRef.current = null
        whisperSegmentChunksRef.current = []

        if (mediaRecorderRef.current) {
            const state = mediaRecorderRef.current.state
            if (state === 'recording' || state === 'paused') {
                mediaRecorderRef.current.stop()
            }
        }
        setIsRecording(false)
        setIsPaused(false)
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
    }

    const remainingTime = maxDuration - duration
    const isNearLimit = remainingTime <= 60 && remainingTime > 0

    return (
        <div className={`flex flex-col gap-4 p-4 bg-base-200 rounded-xl ${className}`}>
            {/* Timer */}
            <div className="text-center">
                <span className={`text-3xl font-mono font-semibold ${isNearLimit ? 'text-warning' : ''}`}>
                    {formatDuration(duration)}
                </span>
                {isRecording && (
                    <div className="text-xs text-base-content/60 mt-1">
                        Limite: {formatDuration(maxDuration)}
                    </div>
                )}
            </div>

            {/* Waveform */}
            <div className="h-20 bg-base-300 rounded-lg overflow-hidden">
                <WaveformVisualizer
                    data={waveformData}
                    isRecording={isRecording}
                    isPaused={isPaused}
                    color={isNearLimit ? '#f59e0b' : '#3b82f6'}
                />
            </div>

            {/* Audio Level Indicator */}
            <div className="h-2 bg-base-300 rounded-full overflow-hidden">
                <div
                    className={`h-full transition-all duration-100 ${isPaused ? 'bg-base-content/30' : 'bg-secondary'
                        }`}
                    style={{ width: `${audioLevel * 100}%` }}
                />
            </div>

            {/* Controls */}
            <div className="flex justify-center">
                <RecordingControls
                    isRecording={isRecording}
                    isPaused={isPaused}
                    onStart={startRecording}
                    onPause={pauseRecording}
                    onResume={resumeRecording}
                    onStop={stopRecording}
                    size="lg"
                />
            </div>

            {/* Status */}
            <RecordingStatus isRecording={isRecording} isPaused={isPaused} />

            {/* Real-time transcription indicator */}
            {enableRealTimeTranscription && isRecording && (
                <div className="flex items-center gap-2 text-xs text-secondary justify-center">
                    <span className="inline-block w-2 h-2 bg-secondary rounded-full animate-pulse"></span>
                    {transcriptionMode === 'whisper'
                        ? 'Transcrição IA (Whisper) ativa'
                        : 'Transcrição em tempo real ativa'}
                </div>
            )}

            {/* Whisper not available warning */}
            {enableRealTimeTranscription && transcriptionMode === 'whisper' && !isConnected && (
                <div className="alert alert-warning text-sm">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        className="stroke-current shrink-0 w-5 h-5"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                    </svg>
                    <span>
                        Conexao com servidor indisponivel. A transcrição sera feita apos o envio do audio.
                    </span>
                </div>
            )}

            {/* Speech recognition not supported warning (browser mode) */}
            {enableRealTimeTranscription && transcriptionMode === 'browser' && !speechRecognitionSupported && (
                <div className="alert alert-info text-sm">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        className="stroke-current shrink-0 w-5 h-5"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                    </svg>
                    <span>
                        Seu navegador nao suporta transcrição em tempo real. O audio sera
                        transcrito apos o envio.
                    </span>
                </div>
            )}

            {/* Permission denied message */}
            {permissionDenied && (
                <div className="alert alert-warning text-sm">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="stroke-current shrink-0 h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                    </svg>
                    <span>
                        O acesso ao microfone foi negado. Clique no icone de cadeado na
                        barra de endereco e permita o acesso ao microfone.
                    </span>
                </div>
            )}

            {/* Near limit warning */}
            {isNearLimit && isRecording && (
                <div className="alert alert-warning text-sm">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="stroke-current shrink-0 h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                    </svg>
                    <span>
                        A gravacao sera finalizada automaticamente em{' '}
                        {formatDuration(remainingTime)}
                    </span>
                </div>
            )}
        </div>
    )
}

export default AudioRecorder
